import { z } from 'zod';
import {
  baseDocumentFields,
  emailLower,
  httpUrl,
  objectIdString,
  phoneE164,
  slug,
} from './common.js';
import { brokerStatus } from './enums.js';

// Each template is optional. When absent the app falls back to the global default
// from the messages catalog. Placeholders supported: {leadName}, {brokerFirstName},
// {propertyName}.
export const whatsappTemplates = z
  .object({
    notQualified: z.string().max(800).optional(),
    partiallyQualified: z.string().max(800).optional(),
    fullyQualified: z.string().max(800).optional(),
  })
  .strict();

export type WhatsappTemplates = z.infer<typeof whatsappTemplates>;

// Personalization fields exposed in the broker's own panel ("Minha página").
// All fields optional so a freshly-created broker has sensible empty defaults.
export const brokerPageContent = z
  .object({
    heroHeadline: z.string().max(120).optional(),
    heroSubheadline: z.string().max(240).optional(),
    aboutText: z.string().max(4000).optional(),
    coverPhotoUrl: httpUrl.optional(),
    featuredPropertyIds: z.array(objectIdString).max(50).default([]),
    socialLinks: z
      .object({
        instagram: httpUrl.optional(),
        facebook: httpUrl.optional(),
        tiktok: httpUrl.optional(),
        youtube: httpUrl.optional(),
      })
      .strict()
      .optional(),
    theme: z
      .object({
        /** Overrides the global brand blue. Used for primary CTAs, headers and headings. */
        primaryColor: z
          .string()
          .regex(/^#[0-9a-fA-F]{6}$/, 'Hex color in #RRGGBB format')
          .optional(),
        /** Decorative accent used in eyebrows, step numbers, trust icons. Independent of primary. */
        accentColor: z
          .string()
          .regex(/^#[0-9a-fA-F]{6}$/, 'Hex color in #RRGGBB format')
          .optional(),
      })
      .strict()
      .optional(),
    /**
     * How to render the broker avatar. 'circle' (default) crops to a round
     * shape with object-cover — works for human portraits. 'square' renders
     * as a soft-rounded rectangle with object-contain on white — preserves
     * the full shape of logos and brand marks (e.g. proteção veicular
     * partners that use shield/crest logos).
     */
    avatarShape: z.enum(['circle', 'square']).optional(),
    /**
     * Endereço físico de atendimento. Quando setado, a landing renderiza
     * uma seção "Onde fica" com o endereço completo, CTAs (Maps, WhatsApp)
     * e (opcionalmente) um mapa estático/embed. Brokers que atendem só
     * online deixam vazio — a seção some.
     */
    address: z.string().trim().max(200).optional(),
    /** URL completa pra abrir no Google Maps (rota). */
    mapLinkUrl: httpUrl.optional(),
  })
  .strict();

export type BrokerPageContent = z.infer<typeof brokerPageContent>;

export const broker = z
  .object({
    ...baseDocumentFields,
    /** Bridge to Firebase Auth · unique. */
    firebaseUid: z.string().min(1),
    /** Unique subdomain slug used to route the public landing. */
    subdomain: slug.min(2).max(30),
    /**
     * Vertical (market segment) this broker operates in.
     *
     * Optional during the multi-vertical rollout: existing brokers may not have
     * it set yet. Backend resolves it via `resolveBrokerVerticalKey()` helper,
     * which falls back to the MCMV-imobiliário vertical when unset. This keeps
     * Angel and Wesley working without any data migration.
     *
     * Will become required once the backfill migration runs.
     */
    verticalId: objectIdString.optional(),
    name: z.string().trim().min(2).max(120),
    email: emailLower,
    phone: phoneE164,
    whatsappNumber: phoneE164,
    bio: z.string().max(1000).optional(),
    avatarUrl: httpUrl.optional(),
    status: brokerStatus,
    /**
     * Override opcional do `content_category` enviado no Meta CAPI para os
     * eventos deste broker. Quando vazio, usa o default declarado na vertical
     * (`vertical.capiContentCategory`). Permite separar o broker no Events
     * Manager / criar Custom Conversions específicas sem precisar de Pixel
     * próprio · ex: 'protecao_veicular_riscozero' isola RZ de outros PVs.
     */
    metaContentCategory: z.string().trim().min(2).max(80).optional(),
    /**
     * `false` = broker criado via seed com senha padrão · modal aparece
     * no painel. Vira `true` quando ele decide (trocar ou manter). Undefined
     * = broker legado tratado como já resolvido (sem modal).
     */
    hasChangedInitialPassword: z.boolean().optional(),
    /**
     * Pixel próprio do broker · carregado ADICIONALMENTE ao Pixel NexoFácil
     * na landing dele. Permite o cliente rodar campanhas na conta Meta dele.
     */
    metaPixelId: z
      .string()
      .trim()
      .regex(/^\d{10,20}$/, 'Meta Pixel ID = só dígitos (10-20 chars)')
      .optional(),
    pageContent: brokerPageContent,
    whatsappTemplates: whatsappTemplates,
  })
  .strict();

export type Broker = z.infer<typeof broker>;
